<template>
    <div class="bg-white rounded shadow min-h-screen">
        <TodaySaleStat :period="period" :currency-short-name="currentCurrencyShortName"/>

        <Row type="none" class="bg-white rounded shadow mt-3">
            <div class="container grid grid-cols-12 gap-3 py-4">
                <div class="col-span-12 lg:col-span-4 relative">
                    <img src="/images/search-interface-symbol.png" alt="" class="h-6 absolute left-2 top-2">
                    <b-form-input
                        class="border-gray-300 pl-10 w-full h-11 focus:ring-0"
                        v-model="search.productName"
                        :placeholder="$t('sale.product.title')"
                    />
                </div>
                <div class="col-span-12 sm:col-span-3 lg:col-start-5 lg:col-span-2">
                    <b-form-select
                        v-model="currentCurrencyShortName"
                        class="h-11 border-gray-300 focus:ring-0"
                    >
                        <b-form-select-option value="UZS">UZS</b-form-select-option>
                        <b-form-select-option value="USD">USD</b-form-select-option>
                        <b-form-select-option value="RUB">RUB</b-form-select-option>
                    </b-form-select>
                </div>
                <div class="col-span-12 sm:col-span-3 lg:col-span-2">
                    <b-form-select
                        v-model="statPeriod"
                        @change="defineStatPeriod"
                        class="h-11 border-gray-300 focus:ring-0"
                    >
                        <b-form-select-option :value="0">{{ $t('statistics.type.statByDay') }}</b-form-select-option>
                        <b-form-select-option :value="1">{{ $t('statistics.type.statByMonth') }}</b-form-select-option>
                        <b-form-select-option :value="2">{{ $t('statistics.type.statByYear') }}</b-form-select-option>
                    </b-form-select>
                </div>
                <div class="col-span-12 sm:col-span-6 lg:col-span-4">
                    <div v-if="isYearly">
                        <b-form-select
                            class="h-11 border-gray-300 focus:ring-0"
                            v-model="selectedYear"
                            :options="optionsYear"
                            @change="yearlyStat">
                        </b-form-select>
                    </div>

                    <div v-if="isMonthly" class="flex gap-3">
                        <b-form-select
                            class="h-11 border-gray-300 focus:ring-0"
                            v-model="selectedYear"
                            @change="monthlyStat"
                            :options="optionsYear">
                        </b-form-select>
                        <b-form-select
                            class="h-11 border-gray-300 focus:ring-0"
                            v-model="selectedMonth"
                            :options="optionsMonth"
                            @change="monthlyStat">
                        </b-form-select>
                    </div>

                    <div v-if="isDaily">
                        <b-form-datepicker
                            :locale="$i18n.locale === 'uz' ? 'ru' : $i18n.locale"
                            style="height: 44px !important;"
                            class="border-gray-300 focus:ring-0"
                            id="example-datepicker"
                            v-model="currentDate"
                            :hide-header="hideHeader"
                            @input="dailyStat"
                        ></b-form-datepicker>
                    </div>
                </div>

                <div class="col-span-6">
                    <b-form-select @change="setLimit" class="border-gray-300 h-11 focus:ring-0" v-model="limit">
                        <b-form-select-option value="all">{{ $t('filter.all') }}</b-form-select-option>
                        <b-form-select-option value="3">3</b-form-select-option>
                        <b-form-select-option value="5">5</b-form-select-option>
                        <b-form-select-option value="10">10</b-form-select-option>
                        <b-form-select-option value="20">20</b-form-select-option>
                        <b-form-select-option value="30">30</b-form-select-option>
                    </b-form-select>
                </div>

                <div class="col-span-6">
                    <b-form-select @change="sortBy" v-model="sortByProduct" class="border-gray-300 h-11 focus:ring-0">
                        <b-form-select-option value="amount">{{ $t('statistics.byAmount') }}</b-form-select-option>
                        <b-form-select-option value="income">{{ $t('statistics.byIncome') }}</b-form-select-option>
                    </b-form-select>
                </div>
            </div>

            <table class="w-full d-none d-md-table">
                <thead class="h-11 bg-lightGray">
                <tr>
                    <td class="px-2 font-bold">№</td>
                    <td class="px-2 font-bold">{{ $t('sale.product.title') }}</td>
                    <td class="px-2 font-bold">{{ $t('amount') }}</td>
                    <td class="px-2 font-bold">{{ $t('sellingPrice') }}</td>
                    <td class="px-2 font-bold">{{ $t('sale.sum') }}</td>
                    <td class="px-2 font-bold">{{ $t('statistics.income') }}</td>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(rate, index) of filteredProducts" :key="index" class="h-11">
                    <td class="px-2">{{ pageNumberMaker(index) }}</td>
                    <td class="px-2 text-main font-bold max-w-2xl">{{ rate.product.name }}</td>
                    <td class="px-2">{{ rate.amount }} {{ rate.product.unitOfMeasure }}</td>
                    <td class="px-2">
                        {{ showCurrencyById(rate.product.sellingPriceCurrency.id, rate.product.sellingPrice) }}
                    </td>
                    <td class="px-2">{{
                            showCurrencyById(rate.product.sellingPriceCurrency.id, rate.amount * parseFloat(rate.product.sellingPrice))
                        }}
                    </td>
                    <td class="px-2">{{
                            showCurrencyById(3, rate.incomeAmount)
                        }}
                    </td>
                </tr>
                </tbody>
            </table>
            <table class="w-full md:hidden">
                <tbody v-for="(rate, index) of filteredProducts" :key="index" style="border-bottom: 5px solid dimgray">
                <tr class="h-11">
                    <td class="px-2 font-weight-bold">id</td>
                    <td class="px-2">{{ pageNumberMaker(index) }}</td>
                </tr>
                <tr class="h-11">
                    <td class="px-2 font-weight-bold">{{ $t('sale.product.title') }}</td>
                    <td class="px-2 font-bold text-main">{{ rate.product.name }}</td>
                </tr>
                <tr class="h-11">
                    <td class="px-2 font-weight-bold">{{ $t('amount') }}</td>
                    <td class="px-2">{{ rate.amount }} {{ rate.product.unitOfMeasure }}</td>
                </tr>
                <tr class="h-11">
                    <td class="px-2 font-weight-bold">{{ $t('sellingPrice') }}</td>
                    <td class="px-2">
                        {{ showCurrencyById(rate.product.sellingPriceCurrency.id, rate.product.sellingPrice) }}
                    </td>
                </tr>
                <tr class="h-11">
                    <td class="px-2 font-weight-bold">{{ $t('sale.sum') }}</td>
                    <td class="px-2">{{
                            showCurrencyById(rate.product.sellingPriceCurrency.id, rate.amount * parseFloat(rate.product.sellingPrice))
                        }}
                    </td>
                </tr>
                <tr class="h-11">
                    <td class="px-2 font-weight-bold">{{ $t('statistics.income') }}</td>
                    <td class="px-2">{{
                            showCurrencyById(3, rate.incomeAmount)
                        }}
                    </td>
                </tr>
                </tbody>
            </table>
        </Row>

        <div class="row mt-4">
            <div class="col">
                <Pagination
                    :search="search"
                    base-url="statistics/product"
                    :total-items="getTotalCount"
                    :method-name="getMethodName"
                />
            </div>
        </div>
    </div>
</template>

<script>
import TodaySaleStat from "@/components/TodaySaleStat.vue";
import {mapActions, mapGetters} from "vuex";
import currencyFormatMixin from "@/mixin/currencyFormatMixin";
import Pagination from "@/components/html/Pagination";
import dateFormattingMixin from "@/mixin/dateFormattingMixin";
import Row from "@/components/html/Row.vue";

export default {
    name: "ProductStat",
    components: {Row, TodaySaleStat, Pagination},
    mixins: [currencyFormatMixin, dateFormattingMixin],
    data() {
        return {
            search: {
                productName: '',
                after: null,
                before: null,
                byIncome: false,
                byAmount: true,
                page: 1,
                isNeedFetch: false
            },
            breadcrumbItems: [
                {name: 'home', url: ''},
                {name: 'statistics.product'},
            ],
            statPeriod: 0,
            isDaily: true,
            isMonthly: false,
            isYearly: false,
            hideHeader: false,
            selectedYear: null,
            selectedMonth: null,
            optionsMonth: [
                {value: {start: '01-01', end: '01-31'}, text: this.$t('statistics.month.Jan')},
                {value: {start: '02-01', end: '02-28'}, text: this.$t('statistics.month.Feb')},
                {value: {start: '03-01', end: '03-31'}, text: this.$t('statistics.month.Mar')},
                {value: {start: '04-01', end: '04-30'}, text: this.$t('statistics.month.Apr')},
                {value: {start: '05-01', end: '05-31'}, text: this.$t('statistics.month.May')},
                {value: {start: '06-01', end: '06-30'}, text: this.$t('statistics.month.Jun')},
                {value: {start: '07-01', end: '07-31'}, text: this.$t('statistics.month.Jul')},
                {value: {start: '08-01', end: '08-31'}, text: this.$t('statistics.month.Aug')},
                {value: {start: '09-01', end: '09-30'}, text: this.$t('statistics.month.Sep')},
                {value: {start: '10-01', end: '10-31'}, text: this.$t('statistics.month.Oct')},
                {value: {start: '11-01', end: '11-30'}, text: this.$t('statistics.month.Nov')},
                {value: {start: '12-01', end: '12-31'}, text: this.$t('statistics.month.Dec')},
            ],
            optionsYear: [],
            currentDate: null,
            currentMonth: null,
            period: '',
            limit: 'all',
            sortByProduct: 'amount',
            productRatings: []
        }
    },
    computed: {
        ...mapGetters([
            'getUserCompanyCreatedAt',
            'getSaleProductDailyRating',
            'getTopProducts',
            'getSaleProductDailyRatingTotalCount',
            'getSaleProductMonthlyRatingTotalCount',
            'getSaleProductYearlyRatingTotalCount',
            'getSaleProductMonthlyRating',
            'getSaleProductYearlyRating',
            'getSaleProductRatingAllPeriod'
        ]),
        filteredProducts() {
            if (this.search.productName !== '') {
                return this.getSaleProductRatingAllPeriod.filter((item) => {
                    return this.search.productName.toLowerCase().split(' ').every(v => item.product.name.toLowerCase().includes(v))
                })
            } else {
                return this.productRatings
            }
        },
        getTotalCount() {
            if (this.isDaily) {
                return this.limit === 'all' ?
                    this.getSaleProductDailyRatingTotalCount : this.limit > this.productRatings.length ?
                        this.getSaleProductDailyRatingTotalCount : this.getSaleProductDailyRatingTotalCount > 10 ?
                            parseInt(this.limit) : this.productRatings.length
            } else if (this.isMonthly) {
                return this.limit === 'all' ?
                    this.getSaleProductMonthlyRatingTotalCount : this.limit > this.productRatings.length ?
                        this.getSaleProductMonthlyRatingTotalCount : this.getSaleProductMonthlyRatingTotalCount > 10 ?
                            parseInt(this.limit) : this.productRatings.length
            } else {
                return this.limit === 'all' ?
                    this.getSaleProductYearlyRatingTotalCount : this.limit > this.productRatings.length ?
                        this.getSaleProductYearlyRatingTotalCount : this.getSaleProductYearlyRatingTotalCount > 10 ?
                            parseInt(this.limit) : this.productRatings.length
            }
        },
        getMethodName() {
            return this.isDaily ? 'fetchSaleProductDailyRating' : this.isMonthly ? 'fetchSaleProductMonthlyRating' : 'fetchSaleProductYearlyRating'
        }
    },
    methods: {
        ...mapActions([
            'fetchUser',
            'fetchTopProducts',
            'fetchCompanyPayTypeStatistics',
            'fetchTopProductsLastPeriod',
            'fetchSaleProductDailyRating',
            'fetchSaleProductMonthlyRating',
            'fetchSaleProductYearlyRating',
            'fetchSaleProductRatingAllPeriod',
            'setBreadcrumbItems'
        ]),
        defineStatPeriod() {
            if (this.statPeriod === 0) {
                this.statByDays()
            } else if (this.statPeriod === 1) {
                this.statByMonth()
            } else {
                this.statByYear()
            }
        },
        pageNumberMaker(index) {
            return  this.$route.params.page === undefined || this.$route.params.page === '1' ?
                (index + 1) : ((this.$route.params.page - 1).toString() + (index + 1).toString()).length === 3 ?
                    (this.$route.params.page * (index + 1)) : ((this.$route.params.page - 1).toString() + (index + 1).toString())
        },
        statByDays() {
            this.isDaily = true
            this.isMonthly = false
            this.isYearly = false
            let date = {dateFrom: this.currentDate + 'T00:00:00', dateTo: this.currentDate + 'T23:59:59', isDaily: true}
            this.$store.state.company.companyPayTypeStatistics.isNeedFetch = true
            this.search.after = this.getTodayDate()
            this.search.before = this.getTodayDate()
            this.currentDate = this.getTodayDate()

            this.periodDefiner(new Date())
            this.fetchCompanyPayTypeStatistics(date)
            this
                .fetchSaleProductDailyRating(this.search)
                .then(() => {
                    this.productRatings = this.getSaleProductDailyRating
                })
        },
        statByMonth() {
            this.isDaily = false
            this.isMonthly = true
            this.isYearly = false
            this.search.before = this.selectedYear + '-' + this.selectedMonth.end
            this.search.after = this.selectedYear + '-' + this.selectedMonth.start

            let date = {
                dateFrom: this.selectedYear + '-' + this.selectedMonth.start + 'T00:00:00',
                dateTo: this.selectedYear + '-' + this.selectedMonth.end + 'T23:59:59',
                isMonthly: true
            }

            this.$store.state.company.companyPayTypeStatistics.isNeedFetch = true

            this.periodDefiner(date.dateFrom, date.dateTo)
            this.fetchCompanyPayTypeStatistics(date)
            this
                .fetchSaleProductMonthlyRating(this.search)
                .then(() => {
                    this.productRatings = this.getSaleProductMonthlyRating
                })
        },
        statByYear() {
            this.isDaily = false
            this.isMonthly = false
            this.isYearly = true
            this.search.after = this.selectedYear + '-' + 1 + '-' + 1
            this.search.before = this.selectedYear + '-' + 12 + '-' + 31
            let date = {dateFrom: this.selectedYear + '-01-01T00:00:00', dateTo: this.selectedYear + '-12-31T23:59:59'}
            this.$store.state.company.companyPayTypeStatistics.isNeedFetch = true

            this.periodDefiner(this.selectedYear, null, true)
            this.fetchCompanyPayTypeStatistics(date)
            this
                .fetchSaleProductYearlyRating(this.search)
                .then(() => {
                    this.productRatings = this.getSaleProductYearlyRating
                })
        },
        dailyStat() {
            this.search.after = this.currentDate + 'T00:00:00'
            this.search.before = this.currentDate + 'T23:59:59'
            let date = {dateFrom: this.currentDate + 'T00:00:00', dateTo: this.currentDate + 'T23:59:59', isDaily: true}
            this.$store.state.company.companyPayTypeStatistics.isNeedFetch = true
            this.$store.state.saleProductDailyRating.saleProductDailyRating.isNeedFetch = true

            this.periodDefiner(date.dateFrom)
            this.fetchCompanyPayTypeStatistics(date)
            this
                .fetchSaleProductDailyRating(this.search)
                .then(() => {
                    this.productRatings = this.getSaleProductDailyRating
                })
        },
        monthlyStat() {
            if (this.selectedYear === null && this.selectedMonth === null) {
                return
            }

            this.search.after = this.selectedYear + '-' + this.selectedMonth.start + 'T00:00:00'
            this.search.before = this.selectedYear + '-' + this.selectedMonth.end + 'T23:59:59'

            let date = {
                dateFrom: this.selectedYear + '-' + this.selectedMonth.start + 'T00:00:00',
                dateTo: this.selectedYear + '-' + this.selectedMonth.end + 'T23:59:59',
                isMonthly: true
            }

            this.$store.state.company.companyPayTypeStatistics.isNeedFetch = true
            this.$store.state.saleProductMonthlyRating.saleProductMonthlyRating.isNeedFetch = true

            this.periodDefiner(date.dateFrom, date.dateTo)
            this.fetchCompanyPayTypeStatistics(date)
            this
                .fetchSaleProductMonthlyRating(this.search)
                .then(() => {
                    this.productRatings = this.getSaleProductMonthlyRating
                })
        },
        yearlyStat() {
            this.search.after = this.selectedYear + '-01-01T00:00:00'
            this.search.before = this.selectedYear + '-12-31T23:59:59'
            let date = {dateFrom: this.selectedYear + '-01-01T00:00:00', dateTo: this.selectedYear + '-12-31T23:59:59'}
            this.$store.state.company.companyPayTypeStatistics.isNeedFetch = true
            this.$store.state.saleProductYearlyRating.saleProductYearlyRating.isNeedFetch = true

            this.periodDefiner(this.selectedYear, null, true)
            this.fetchCompanyPayTypeStatistics(date)
            this
                .fetchSaleProductYearlyRating(this.search)
                .then(() => {
                    this.productRatings = this.getSaleProductYearlyRating
                })
        },
        bgColorDefiner() {
            if (parseInt(this.$route.params.page) === 1 || this.$route.params.page === undefined) {
                return 'background-color: #B6FADD; height: 50px;';
            } else if (parseInt(this.$route.params.page) === 2) {
                return 'background-color: #F4ECA5; height: 50px;';
            } else {
                return 'background-color: #FFFFFF; height: 50px;';
            }
        },
        sortBy() {
            this.search.byAmount = this.sortByProduct === 'amount';

            if (this.isDaily) {
                this.$store.state.saleProductDailyRating.saleProductDailyRating.isNeedFetch = true
                this.fetchSaleProductDailyRating(this.search)
                    .then(() => {
                        this.productRatings = this.getSaleProductDailyRating
                    })
            } else if (this.isMonthly) {
                this.$store.state.saleProductMonthlyRating.saleProductMonthlyRating.isNeedFetch = true
                this.fetchSaleProductMonthlyRating(this.search)
                    .then(() => {
                        this.productRatings = this.getSaleProductMonthlyRating
                    })
            } else {
                this.$store.state.saleProductYearlyRating.saleProductYearlyRating.isNeedFetch = true
                this.fetchSaleProductYearlyRating(this.search)
                    .then(() => {
                        this.productRatings = this.getSaleProductYearlyRating
                    })
            }
        },
        setLimit() {
            if (this.limit === 'all') {
                if (this.isDaily) {
                    this.productRatings = this.getSaleProductDailyRating
                } else if (this.isMonthly) {
                    this.productRatings = this.getSaleProductMonthlyRating
                } else {
                    this.productRatings = this.getSaleProductYearlyRating
                }

                return
            }

            if (this.limit < this.productRatings.length) {
                this.productRatings = this.productRatings.slice(0, this.limit)
            } else {
                if (this.isDaily) {
                    this.productRatings = this.getSaleProductDailyRating
                } else if (this.isMonthly) {
                    this.productRatings = this.getSaleProductMonthlyRating
                } else {
                    this.productRatings = this.getSaleProductYearlyRating
                }

                this.productRatings = this.productRatings.slice(0, this.limit)
            }
        },
        setDate() {
            if (this.search.after !== null && this.search.before !== null) {
                this.fetchSaleProductDailyRating(this.search)
                this.periodDefiner(this.search.after, this.search.before)
            }
        },
        getTodayDate() {
            let yearNow = new Date().getFullYear()
            let monthNow = new Date().getMonth() + 1
            let dateNow = new Date().getDate()

            return yearNow + '-' + this.addZero(monthNow) + '-' + this.addZero(dateNow)
        },
        addZero(val) {
            return val < 10 ? '0' + val : val;
        },
        periodDefiner(from, to = null, isYear = false) {
            if (isYear) {
                this.period = from.toString()
                return
            }

            if (to === null) {
                this.period = this.dateFormat(from)
            } else {
                let start = this.dateFormat(from)
                let end = this.dateFormat(to)
                this.period = start + ' - ' + end
            }
        },
        async createYear() {
            let created = parseInt(this.getUserCompanyCreatedAt)

            while (created <= new Date().getFullYear()) {
                this.optionsYear.unshift({value: created, text: created})
                created++
            }

            await this.setYear()
        },
        setYear() {
            this.selectedYear = this.optionsYear[0].value
        },
        dateSwitcher() {
            this.selectedMonth = this.optionsMonth[this.currentMonth - 1].value
        },
    },
    beforeMount() {
        this.search.before = this.getTodayDate() + ' 23:59:59'
        this.search.after = this.getTodayDate() + ' 00:00:00'
    },
    created() {
        this.setBreadcrumbItems(this.breadcrumbItems)
    },
    mounted() {
        this.$root.showOverlay = true
        this
            .fetchUser()
            .then(() => {
                this.currentMonth = new Date().getUTCMonth() + 1
                this.currentDate = this.getTodayDate()

                this.createYear()
                this.dateSwitcher()
                this.statByDays()
                this.fetchSaleProductRatingAllPeriod()
                    .then(() => {
                        this.$root.showOverlay = false
                        this.$i18nHelper.flash(this.$t('statistics.info.gap'), 'info')
                    })
                    .catch((error) => {
                        console.log(error)
                        this.$root.showOverlay = false
                    })
                this.periodDefiner(this.search.after, this.search.before)
            })
            .catch((error) => {
                console.log(error)
                this.$root.showOverlay = false
            })
    },
    watch: {
        getSaleProductDailyRating() {
            this.productRatings = this.getSaleProductDailyRating
        },
        getSaleProductMonthlyRating() {
            this.productRatings = this.getSaleProductMonthlyRating
        },
        getSaleProductYearlyRating() {
            this.productRatings = this.getSaleProductYearlyRating
        }
    }
}
</script>

<style scoped>

table tbody tr {
    border-bottom: 1px solid #eaeaea;
}
</style>
